
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Preset, UserCpuResult } from '@/services/Usage/interfaces';
import UsageRepository from '@/services/Usage';
import CpuLineChart from '@/views/pages/usage/components/CpuLineChart.vue';

@Component({
  inject: ['usageRepository'],
  components: { CpuLineChart },
})
export default class CpuTab extends Vue {
  private readonly usageRepository!: UsageRepository;

  @Prop({ required: true }) readonly systemUser!: string;

  private loading = false;
  private usage: UserCpuResult | null = null;
  private error: string | null = null;
  private selectedPreset: Preset = '24h';

  get summaryText(): string {
    if (!this.usage) {
      return '';
    }

    const totalNs = this.usage.points.reduce((sum, p: any) => {
      return sum + Number(p.value || 0);
    }, 0);

    const mins = totalNs / 60_000_000_000;
    return `${mins.toFixed(1)} min used`;
  }

  get chartPoints(): Array<{ x: Date; y: number }> {
    if (!this.usage) return [];

    const step = Number((this.usage as any).stepSeconds ?? 3600);
    const denom = step * 1e9;

    return this.usage.points.map((p: any) => ({
      x: new Date(Number(p.timestampUnixSeconds) * 1000),
      y: Number(p.value) / denom,
    }));
  }

  async mounted(): Promise<void> {
    if (this.systemUser) {
      await this.load();
    }
  }

  @Watch('selectedPreset')
  private async onRangeChanged(): Promise<void> {
    if (!this.systemUser) return;
    await this.load();
  }

  @Watch('systemUser')
  private async onSystemUserChanged(): Promise<void> {
    if (!this.systemUser) return;
    await this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;

    try {
      this.usage = await this.usageRepository.getCpuUsage(this.systemUser, this.selectedPreset);
    } catch (e: any) {
      this.usage = null;
      this.error = e?.message || 'Failed to load data';
    } finally {
      this.loading = false;
    }
  }
}
